import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { cacheStore } from "@/Globals/Stores/CacheStore";
import { IModuleInitializerParams, ModuleInitializer } from "../ModuleInitializer";

/**
 * The modules context, including the loaded modules and the last affected modules.
 */
interface IReadonlyModulesContext {
    readonly loadedModules: Set<string>;
    readonly lastAffectedModules: Set<string>;
}

/**
 * The modules context with a method to set the loaded modules.
 */
interface IModulesContext extends IReadonlyModulesContext {
    setLoadedModules: (loadedModules: Set<string>) => void
}

/**
 * Context to manage the state of loaded modules and their context refresh counts.
 * Provides a way to access the loaded modules and trigger re-renders when the context changes.
 * It is meant to be read by the useComponentRegistry hook to trigger component registry re-renders.
 */
const ModulesContext = createContext<IModulesContext>({
    loadedModules: new Set(),
    lastAffectedModules: new Set(),
    setLoadedModules: () => {}
});

/**
 * Custom hook to access the modules context.
 * This is used by the useComponentRegistry hook to trigger re-renders when the modules context changes.
 * @returns The modules context with loaded modules and last affected modules.
 */
function useModulesContext(): IReadonlyModulesContext {
    const context = useContext(ModulesContext);
    return {
        loadedModules: context.loadedModules,
        lastAffectedModules: context.lastAffectedModules
    };
}

/**
 * Webpack's RequireModule type definition.
 * It is used to require modules dynamically based on their directory and regular expression.
 */
type RequireModule = {
    (id: string): any;
    keys(): string[];
};

/**
 * Webpack's require context type definition.
 * It allows to require modules dynamically based on their directory and regular expression.
 * This is used to load modules from the src/Modules directory.
 */
declare const require: {
    context(directory: string, useSubdirectories: boolean, regExp: RegExp): RequireModule;
};

/**
 * Custom hook to load modules dynamically.
 * It checks if the module parameters have changed and reloads the modules if necessary.
 * It also updates the loaded modules state in the context.
 */
function useModulesLoader(): void {
    const lastParamsString = useRef<string>("");
    const loadedModules = useRef<Set<string>>(new Set());
    const modulesContext = useContext(ModulesContext);

    const loadModules = useCallback(async () => {
        const getCurrentParams = (): IModuleInitializerParams => ({
            clinicSettings: cacheStore.ClinicSettings,
            clinicSites: cacheStore.SiteCache,
            currentEmployee: sessionStore.getCurrentEmployee()
        });

        const checkIfUpdateNeeded = (currentParams: IModuleInitializerParams): boolean => {
            const currentParamsString = JSON.stringify(currentParams);
            const shouldReloadModule = currentParamsString !== lastParamsString.current;
            lastParamsString.current = currentParamsString;

            return shouldReloadModule;
        };

        const updateModuleState = (moduleName: string, isLoaded: boolean): boolean => {
            const wasLoaded = loadedModules.current.has(moduleName);
            const isModuleLoadingStateChanged = isLoaded !== wasLoaded;

            if (isLoaded)
                loadedModules.current.add(moduleName);
            else
                loadedModules.current.delete(moduleName);

            return isModuleLoadingStateChanged;
        };

        const processModule = async (key: string, requireModule: RequireModule, currentParams: IModuleInitializerParams): Promise<boolean> => {
            // extract module name from the module folder (e.g., "@/Modules/ModuleName/init.ts" -> "ModuleName")
            const moduleName = key.replace(/^\.\/([^/]+)\/init.ts$/, "$1");

            try {
                const moduleInit = requireModule(key);
                const initFunction = moduleInit.default as ModuleInitializer;

                if (typeof initFunction !== "function")
                    throw new Error(`Module ${moduleName} does not export a valid initializer function.`);

                const isLoaded = await initFunction(currentParams);
                const isModuleLoadingStateChanged = updateModuleState(moduleName, isLoaded);

                if (isModuleLoadingStateChanged)
                    modulesContext.setLoadedModules(new Set(loadedModules.current));

                return isModuleLoadingStateChanged;
            }
            catch (error) {
                console.error(`Error loading module ${moduleName}:`, error);
                return false;
            }
        };

        const currentParams = getCurrentParams();
        const shouldReloadModule = checkIfUpdateNeeded(currentParams);

        if (!shouldReloadModule)
            return; // no change, skip reload

        const requireModule = require.context(
            "@/Modules", // search in src/Modules directory
            true, // include subdirectories
            /^\.\/[^/]+\/init.ts$/ // only init files in direct child directories
        );

        let moduleUpdateCount = 0;
        const keys = requireModule.keys();

        for (const key of keys) {
            const isModuleLoadingStateChanged = await processModule(key, requireModule, currentParams);
            if (isModuleLoadingStateChanged)
                moduleUpdateCount++;
        }

        if (moduleUpdateCount > 0)
            console.debug("Loaded modules:", Array.from(loadedModules.current).join(", "));
    }, [modulesContext]);

    useEffect(() => {
        loadModules();
    // loadModules should only be called once on mount in this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useOnStoreChange(cacheStore, () => {
        loadModules();
    });

    useOnStoreChange(sessionStore, () => {
        loadModules();
    });
}

/**
 * Props for the ModulesProvider component.
 * It allows children components to access the modules context.
 */
interface IModulesProviderProps {
    readonly children?: React.ReactNode;
}

/**
 * Provider component for the ModulesContext.
 * It manages the state of loaded modules.
 * It also takes care of loading modules using the useModulesLoader hook.
 * Children components can access the modules context through this provider.
 * @param props The props for the ModulesProvider component.
 * @returns A JSX element that provides the ModulesContext to its children.
 */
function ModulesProvider(props: IModulesProviderProps): JSX.Element {
    const loadedModulesRef = useRef<Set<string>>(new Set());
    const [lastAffectedModules, setLastAffectedModules] = useState<Set<string>>(new Set());

    const setLoadedModules = useCallback((loadedModules: Set<string>): void => {
        const affectedModules = loadedModules.symmetricDifference(loadedModulesRef.current);
        loadedModulesRef.current = loadedModules;

        if (affectedModules.size > 0)
            setLastAffectedModules(new Set(affectedModules));
    }, []);

    const contextValue = useMemo(() => ({
        loadedModules: loadedModulesRef.current,
        lastAffectedModules,
        setLoadedModules
    }), [lastAffectedModules, setLoadedModules]);

    return (
        <ModulesContext.Provider value={contextValue}>
            <ModulesLoader />
            {props.children}
        </ModulesContext.Provider>
    );
}

/**
 * Loader component to trigger the useModulesLoader hook.
 * It does not render anything.
 * This is intended to be used inside the ModulesProvider to access the ModulesContext.
 */
function ModulesLoader(): null {
    useModulesLoader();
    return null;
}

export { ModulesProvider, useModulesContext };
